import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const Sidebar = ({ isOpen = false, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, organization, orgLoading, signOut } = useAuth();
  const [isUpgradeCardVisible, setIsUpgradeCardVisible] = useState(true);

  const crmItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', badge: null },
    { label: 'Leads', path: '/leads', icon: 'UserPlus', badge: null },
    { label: 'Accounts', path: '/accounts', icon: 'Building2', badge: null },
    { label: 'Contacts', path: '/contacts', icon: 'Users', badge: null },
    { label: 'Jobs', path: '/jobs', icon: 'Briefcase', badge: null },
    { label: 'Dispatch', path: '/dispatch', icon: 'Truck', badge: null },
    { label: 'Deals', path: '/deals', icon: 'Target', badge: null },
    { label: 'Pipeline', path: '/pipeline', icon: 'GitBranch', badge: null },
    { label: 'Calendar', path: '/calendar', icon: 'CalendarDays', badge: null },
    { label: 'Activities', path: '/activities', icon: 'Activity', badge: null },
    { label: 'Tasks', path: '/tasks', icon: 'CheckSquare', badge: null },
    { label: 'Emails', path: '/emails', icon: 'Mail', badge: null },
    { label: 'Reviews', path: '/review-requests', icon: 'Star', badge: null },
    { label: 'Reports', path: '/reports', icon: 'BarChart3', badge: null },
    { label: 'Templates', path: '/templates', icon: 'FileText', badge: null },
  ];

  const aiSuiteItems = [
    { label: 'AI Voice', path: '/ai-voice', icon: 'Phone', badge: null },
    { label: 'AI Marketing', path: '/ai-marketing', icon: 'Zap', badge: null },
    { label: 'Website Builder', path: '/website-builder', icon: 'Globe', badge: null },
  ];

  const settingsItems = [
    { label: 'Settings', path: '/settings', icon: 'Settings', badge: null },
    { label: 'Billing', path: '/billing', icon: 'CreditCard', badge: null },
    { label: 'Integrations', path: '/integrations', icon: 'Puzzle', badge: null },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleWebsiteBuilder = async () => {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (session) {
      const url = `https://app.wilcart.com?token=${session?.access_token}&refresh=${session?.refresh_token}`;
      window.open(url, '_blank');
    } else {
      window.location.href = '/login';
    }
    if (onClose) onClose();
  };

  const handleUpgradeClick = () => {
    navigate('/billing');
    if (onClose) onClose();
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const renderNavItem = (item, isAiSuite = false) => {
    const isActive = location?.pathname === item?.path;
    return (
      <button
        key={item?.path}
        onClick={() => item?.path === '/website-builder' ? handleWebsiteBuilder() : handleNavigation(item?.path)}
        className={`
          w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg
          transition-smooth group
          ${
            isActive
              ? isAiSuite
                ? 'bg-violet-600 text-white shadow-sm'
                : 'bg-primary text-primary-foreground shadow-sm'
              : isAiSuite
                ? 'text-violet-500 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }
        `}
        aria-current={isActive ? 'page' : undefined}
      >
        <div className="flex items-center space-x-3">
          <Icon
            name={item?.icon}
            size={18}
            className={
              isActive
                ? isAiSuite ? 'text-white' : 'text-primary-foreground'
                : isAiSuite ? 'text-violet-500 dark:text-violet-400 group-hover:text-violet-700 dark:group-hover:text-violet-300' : 'text-muted-foreground group-hover:text-foreground'
            }
          />
          <span>{item?.label}</span>
        </div>
        {item?.badge && (
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
            isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-accent text-accent-foreground'
          }`}>
            {item?.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-background border-r border-border z-50 lg:z-30
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-3 min-w-0">
              <img
                src="/assets/images/wilcart_logo_1-1775846328647.png"
                alt="Wilcart CRM Logo"
                className="h-8 w-auto object-contain"
              />
              <span className="text-lg font-bold text-foreground truncate">Wilcart</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="lg:hidden flex-shrink-0"
              aria-label="Close navigation menu"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {/* CRM Section */}
            <div className="px-3 space-y-1">
              {crmItems?.map(item => renderNavItem(item, false))}
            </div>

            {/* AI Suite Section */}
            <div className="px-3 mt-4">
              <div className="flex items-center space-x-2 px-3 py-2 mb-1">
                <div className="flex-1 h-px bg-violet-200 dark:bg-violet-800/50" />
                <span className="text-xs font-semibold text-violet-400 dark:text-violet-500 uppercase tracking-wider whitespace-nowrap">AI Suite</span>
                <div className="flex-1 h-px bg-violet-200 dark:bg-violet-800/50" />
              </div>
              <div className="space-y-1">
                {aiSuiteItems?.map(item => renderNavItem(item, true))}
              </div>
            </div>

            {/* Settings Section */}
            <div className="px-3 mt-4">
              <div className="flex items-center space-x-2 px-3 py-1 mb-1">
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-1">
                {settingsItems?.map(item => renderNavItem(item, false))}
              </div>
            </div>

            {/* Trash */}
            <div className="px-3 mt-2 pt-2 border-t border-border">
              <button
                onClick={() => handleNavigation('/trash')}
                className={`
                  w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg
                  transition-smooth group
                  ${location?.pathname === '/trash' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}
                `}
              >
                <div className="flex items-center space-x-3">
                  <Icon name="Trash2" size={18} className={location?.pathname === '/trash' ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'} />
                  <span>Trash</span>
                </div>
              </button>
            </div>
          </nav>

          {/* Upgrade Card */}
          {!orgLoading && isUpgradeCardVisible && !(['active', 'trialing']?.includes(organization?.subscription_status)) && (
            <div className="p-4 border-t border-border">
              <div className="relative bg-gradient-to-br from-primary to-secondary p-4 rounded-lg text-primary-foreground">
                <button
                  onClick={() => setIsUpgradeCardVisible(false)}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-smooth"
                  aria-label="Close upgrade card"
                >
                  <Icon name="X" size={14} />
                </button>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="Crown" size={20} />
                    <span className="font-semibold">Choose Your Plan</span>
                  </div>
                  <p className="text-sm text-primary-foreground/90">
                    Get AI Voice, Website Builder, and Marketing tools. Plans start at $29/mo.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleUpgradeClick}
                    className="w-full bg-white/20 hover:bg-white/30 text-primary-foreground border-white/30"
                  >
                    <Icon name="ArrowRight" size={16} className="mr-2" />
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-primary-foreground">
                  {getInitials(profile?.full_name)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{profile?.full_name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">{profile?.email || ''}</p>
              </div>
            </div>
            {/* Admin Panel link — only for platform admin */}
            {profile?.is_platform_admin && (
              <button
                onClick={() => handleNavigation('/super-admin')}
                className={`w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg mb-1 transition-smooth ${
                  location?.pathname?.startsWith('/super-admin')
                    ? 'bg-violet-600 text-white' : 'text-violet-500 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30'
                }`}
              >
                <Icon name="Shield" size={16} />
                <span>Admin Panel</span>
              </button>
            )}
            {/* CMD+K hint */}
            <button
              onClick={() => {
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true });
                window.dispatchEvent(event);
                if (onClose) onClose();
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Icon name="Search" size={14} />
                <span>Search</span>
              </div>
              <kbd className="bg-muted border border-border px-1.5 py-0.5 rounded text-xs">⌘K</kbd>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-smooth mt-1"
            >
              <Icon name="LogOut" size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;